import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import './index.css';

import MapboxAutocomplete from "react-mapbox-autocomplete";
import { AddressAutofill } from '@mapbox/search-js-react';

import { useApolloClient, useMutation } from "@apollo/client";

import { RadioButton } from "primereact/radiobutton";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Carousel } from "primereact/carousel";

/** components */
import Map from "../../../components/map";

/** contexts */
import { useSettings } from "../../../context/settings";
import { useUser } from "../../../context/users";

/** QUERIES MUTATIONS */
import { AVAILABLE_SERVICES, SERVICE_PRICE } from "../../../provider/Services/query";
import { PASSENGERS } from "../../../provider/Passengers/query";

/** utils */
import { distanteFormat, getGraphsqErros, maskPhone, timeSecondsConvert } from "../../../utilities/functions";
import config from "../../../config";
import { getAddress, getCordinates } from "../../../services/geocode";
import { Dropdown } from "primereact/dropdown";
import FRANCHISES from "../../../provider/Franchises/query";
import { REGIONS } from "../../../provider/Regions/query";
import { CREATE_BOOKING } from "../../../provider/Bookings/mutation";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";

const Crud = () => {
  const settings = useSettings();
  const apollo = useApolloClient();

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;
  const is_callcenter = user?.getUser()?.is_callcenter;
  const MAPBOX_KEY = config.MAPBOX_KEY;

  const [createBooking] = useMutation(CREATE_BOOKING);

  const toast = React.useRef<any>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [creatingBooking, setCreatingBooking] = useState(false);

  const initialCords: iCordinates = {
    latitude: -70.9,
    longitude: 42.35,
  }

  const [services, setServices] = useState<iServices[]>([]);
  const [selectedService, setSelectedService] = useState('');
  const [race, setRace] = useState<any>();

  const [localCordinates, setLocalCordinates] = useState<iCordinates>(initialCords);
  const [originCordinates, setOriginCordinates] = useState<iCordinates>();
  const [originShowMap, setOriginShowMap] = useState<boolean>(false);
  const [originAddress, setOriginAddress] = useState('');
  const [destinyCordinates, setDestinyCordinates] = useState<iCordinates>();
  const [destinyShowMap, setDestinyShowMap] = useState<boolean>(false);
  const [destinyAddress, setDestinyAddress] = useState('');
  const [stopShowMap, setStopShowMap] = useState<boolean>(false);
  const [stops, setStops] = useState<iStop[]>([]);
  const [stopIndex, setStopIndex] = useState(0);

  const [passengers, setPassengers] = useState<iPassenger[]>();
  const [selectedPassenger, setSelectedPassenger] = useState<iPassenger>();
  const [loadingPassengers, setLoadingPassengers] = useState(true);
  
  const [franchises, setFranchises] = useState<iFranchise[]>();
  const [selectedFranchise, setSelectedFranchise] = useState<iFranchise>();
  const [loadingFranchises, setLoadingFranchises] = useState(true);
  
  const [regions, setRegions] = useState<iRegion[]>();
  const [selectedRegion, setSelectedRegion] = useState<iRegion>();
  const [loadingRegions, setLoadingRegions] = useState(true);

  const [paymentType, setPaymentType] = useState('Dinheiro');
  const [loadMessage, setLoadMessage] = useState('Preencha todos os campos...');

  const [passengerName, setPassegnerName] = useState('');
  const [passengerPhone, setPassengerPhone] = useState('');

  const stopsLimit = 4; // Limite de paradas

  const loadPassengers = () => {
    setLoadingPassengers(true)
    
    const filter: any = {};

    if (franchise_id) filter.franchise_id = Number(franchise_id);

    apollo
      .query({
        query: PASSENGERS,
        variables: {
          filter,
        },
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.passengers;

        setPassengers(data);
        setLoadingPassengers(false);
      })
      .catch(error => {
        console.log('PASSENGERS error: ', JSON.stringify(error));
        setPassengers([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
      });
  };

  const loadFranchises = () => {
    setLoadingFranchises(true)

    apollo
      .query({
        query: FRANCHISES,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.franchises;

        setFranchises(data);
        setLoadingFranchises(false);
      })
      .catch(error => {
        console.log('FRANCHISES error: ', JSON.stringify(error));
        setFranchises([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
      });
  };

  const loadRegions = () => {
    setLoadingRegions(true)

    const variables: any = {}

    if (franchise_id) variables.franchise_id = Number(franchise_id);

    apollo
      .query({
        query: REGIONS,
        variables,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.regions;

        setRegions(data);
        setLoadingRegions(false);
      })
      .catch(error => {
        console.log('REGIONS error: ', JSON.stringify(error));
        setRegions([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
      });
  };

  async function loadServices() {
    const variables: any = { not_negotiable: true, is_callcenter: true };

    if (franchise_id) variables.franchise_id = Number(franchise_id);

    if (acting_region_id) variables.acting_region_id = Number(acting_region_id);

    apollo
      .query({
        query: AVAILABLE_SERVICES,
        variables,

        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.services;
        setServices(data);
      })
      .catch(e => {
        setLoading(false);
        console.log('AVAILABLE_SERVICES error: ', JSON.stringify(e));
        settings.setErrorMessage(getGraphsqErros(e));
        settings.setVisibleError(true);
      });
  }

  function loadServicePrice() {
    setRace(null);

    if (originAddress === '' || !originCordinates) {
      setLoadMessage('Informe o endereço de origem...')
      return;
    }

    if (destinyAddress === '' || !destinyCordinates) {
      setLoadMessage('Informe o endereço de destino...')
      return;
    }

    if (selectedService === '') {
      setLoadMessage('Selecione um serviço...')
      return;
    }

    if (!is_callcenter && !selectedPassenger) {
      setLoadMessage('Selecione um passageiro...')
      return;
    }

    if (!is_callcenter && !franchise_id && !selectedFranchise) {
      setLoadMessage('Selecione uma Franquia...')
      return;
    }

    if (!is_callcenter && !acting_region_id && !selectedRegion) {
      setLoadMessage('Selecione uma Região...')
      return;
    }

    setLoadMessage('Calculando tarifa...')

    const data: any = {
      latitude: originCordinates.latitude,
      longitude: originCordinates.longitude,
      destinationLatitude: destinyCordinates.latitude,
      destinationLongitude: destinyCordinates.longitude,
      serviceId: Number(selectedService),
    }

    if (stops.length > 0) data.stops = { stops };

    apollo
      .query({
        query: SERVICE_PRICE,
        variables: { data },
        fetchPolicy: "no-cache",
      })
      .then(response => {
        if (response.data.availableServices.length > 0) {
          setRace(response.data.availableServices[0]);
        }
        setLoading(false);
        setLoadMessage('');
      })
      .catch(e => {
        console.log('SERVICE_PRICE error: ', JSON.stringify(e))
        settings.setErrorMessage("Não foi possível carregar a tarifa base", getGraphsqErros(e));
        settings.setVisibleError(true);
        setLoading(false);
      });
  }

  const handleCreateBooking = async () => {
    setCreatingBooking(true);
    const payload: any = {};

    const service = services.find((item: iServices) => item.id === selectedService);

    payload.passenger_id = parseInt(`${is_callcenter ? user.getUser()?.passenger_id : selectedPassenger?.id}`);
    payload.offered_value = parseInt(`${race.price * 100}`);
    payload.original_value = parseInt(`${race.price * 100}`);
    payload.offered_value = parseInt(`${race.price * 100}`);
    payload.base_rate = parseInt(`${race.price * 100}`);
    payload.minimum_price = parseInt(`${race.price * 100}`);
    payload.cost_per_minute = service?.cost_per_minute;
    payload.cost_per_km = service?.cost_per_km;
    payload.acting_region_id = parseInt(`${is_callcenter ? user.getUser()?.actingRegion?.id : selectedRegion?.id || acting_region_id}`);
    payload.distance = parseInt(`${race.distance}`);
    payload.routeTime = parseInt(`${race.routeTime}`);
    payload.state = 'AWAIT';
    payload.origin_latitude = originCordinates?.latitude;
    payload.origin_longitude = originCordinates?.longitude;
    payload.destiny_latitude = destinyCordinates?.latitude;
    payload.destiny_longitude = destinyCordinates?.longitude;
    payload.destiny_address = destinyAddress;
    payload.origin_address = originAddress;
    payload.payment_type = paymentType;
    payload.is_callcenter = true;
    payload.service_id = parseInt(`${service?.id}`);

    const stopsObject: any = { passenger: {} };

    if (stops.length > 0) stopsObject.data = stops;

    if (passengerName !== '') stopsObject.passenger.name = passengerName;

    if (passengerPhone !== '') stopsObject.passenger.phone = passengerPhone;

    payload.stops = stopsObject;

    payload.franchise_id = parseInt(`${is_callcenter ? user.getUser()?.franchise?.id : Number(selectedFranchise?.id || franchise_id)}`);

    createBooking({ variables: { data: payload } })
      .then(_response => {
        setCreatingBooking(false);
        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Corrida solicitada, aguarde um motorista", life: 6000 });
        setTimeout(() => {
          document.location.href = '/monitor';
        }, 6000)
      })
      .catch(e => {
        setCreatingBooking(false);
        console.log('createBooking error: ', JSON.stringify(e));
        settings.setErrorMessage("Não foi possível criar a corrida", getGraphsqErros(e));
        settings.setVisibleError(true);
      });
  };

  const getOriginCordinates = async (address: string) => {
    const response = await getCordinates(address);

    setOriginCordinates(response);
  }

  const getOriginAddress = async (cordinates: iCordinates) => {
    const response = await getAddress(cordinates);

    setOriginShowMap(false);

    if (response?.features[0]?.place_name) {
      setOriginAddress(response.features[0].place_name);
    }
  }

  const getDestinyCordinates = async (address: string) => {
    const response = await getCordinates(address);

    setDestinyCordinates(response);
  }

  const getDestinyAddress = async (cordinates: iCordinates) => {
    const response = await getAddress(cordinates);

    setDestinyShowMap(false);

    if (response?.features[0]?.place_name) {
      setDestinyAddress(response.features[0].place_name);
    }
  }

  const getStopAddress = async (index: number) => {
    if (!stops[index]) return;

    const cordinates: iCordinates = {
      latitude: stops[index].latitude,
      longitude: stops[index].longitude,
    }

    const response = await getAddress(cordinates);

    setStopShowMap(false);

    if (response?.features[0]?.place_name) {
      stops[index].address = response.features[0].place_name;
      loadServicePrice();
    }
  }

  const hideDialog = () => {
    setOriginShowMap(false);
    setDestinyShowMap(false);
  };

  const _renderAddStopItem = () => {
    let stopsQuant = stops?.length || 0;

    if (stopsQuant > stopsLimit) stopsQuant = stopsLimit;

    const items: any = [];

    for (let i = 0; i <= stopsQuant; i += 1) {
      if (stops && stops[i])
        items.push(
          <div key={stops[i].address + i} className="card">
            <h5>Parada {i + 1}</h5>
            <MapboxAutocomplete
              style={{width: '100%'}}
              publicKey={MAPBOX_KEY}
              inputClass="form-control search"
              country="br"
              resetSearch={false}
              placeholder={stops[i].address === '' ? "Digite o endereço" : stops[i].address}
              onSuggestionSelect={async (e) => {
                stops[i].address = e;
                const response = await getCordinates(e);
                stops[i].latitude = response?.latitude;
                stops[i].longitude = response?.longitude;
                loadServicePrice();
              }}
            />
            <Button
              label={"Definir local no mapa"}
              disabled={loading}
              icon={loading ? "pi pi-spin pi-spinner" : "pi pi-map-marker"}
              className="p-button-rounded p-button-outlined mr-2 mb-2"
              onClick={() => {
                setStopIndex(i);
                setStopShowMap(true);
              }}
            />
            <Button
              label={"Remover"}
              disabled={loading}
              icon={"pi pi-minus-circle"}
              className="p-button-rounded p-button-danger p-button-outlined mr-2 mb-2"
              onClick={() => {
                const result = stops.filter((_stop, index) => index !== i);
                setStops(result);
              }}
            />
          </div>
        )
    }

    if (stopsQuant < stopsLimit) items.push(
      <Button
        label={"Adicionar Parada"}
        disabled={loading}
        icon="pi pi-plus-circle"
        className="p-button-text"
        onClick={() => {
          setStops((prev) => [...prev, {
            address: '',
            latitude: localCordinates.latitude,
            longitude: localCordinates.longitude,
          }]);
        }}
      />
    )

    return items;
  }

  const _renderServices = (service: iServices) => (
    <div className="product-item">
      <div className="product-item-content"
        onClick={() => setSelectedService(`${service.id}`)}
        style={{
          cursor: 'pointer',
          margin: 5,
          padding: 10,
          border: `1px solid ${`${selectedService}` === `${service.id}` ? 'green' : 'orange'}`,
          borderRadius: 15,
          borderColor: 'orange',
        }}
      >
        <div className="mb-3">
          <img
            src={service.image_path}
            alt={service.name}
            width="100"
            height={100}
            style={{ 
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
            className="product-image"
          />
        </div>
        <div style={{
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'row',
          whiteSpace: 'pre-wrap',
        }}>
          <span>{service.description}{' '}</span>
          <i style={{color: 'orange'}} className="pi pi-user" />
          <span>{' '}{service.capacity}</span>
        </div>
      </div>
    </div>
  );

  const originMapDialogFooter = (
    <>
      <Button
        label={loading ? "Confirmando..." : "Confirmar"}
        disabled={loading}
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => {
          if (originCordinates) getOriginAddress(originCordinates);
        }}
      />
    </>
  );

  const destinyMapDialogFooter = (
    <>
      <Button
        label={loading ? "Confirmando..." : "Confirmar"}
        disabled={loading}
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => {
          if (destinyCordinates) getDestinyAddress(destinyCordinates);
        }}
      />
    </>
  );

  const stopMapDialogFooter = (
    <>
      <Button
        label={loading ? "Confirmando..." : "Confirmar"}
        disabled={loading}
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => {
          if (stops[stopIndex]) getStopAddress(stopIndex);
        }}
      />
    </>
  );

  const carouselResponsiveOptions = [
    {
      breakpoint: "1024px",
      numVisible: 3,
      numScroll: 3,
    },
    {
      breakpoint: "768px",
      numVisible: 2,
      numScroll: 2,
    },
    {
      breakpoint: "560px",
      numVisible: 1,
      numScroll: 1,
    },
  ];

  useEffect(() => {
    loadServices();

    const cookieOriginCordinates = Cookies.get('@originCordinates');
    if (cookieOriginCordinates) setOriginCordinates(JSON.parse(cookieOriginCordinates));

    const cookieOriginAddress = Cookies.get('@originAddress');
    if (cookieOriginAddress) setOriginAddress(cookieOriginAddress);
    
    if (!is_callcenter) {
      loadPassengers()

      if (!franchise_id) loadFranchises();

      if (!acting_region_id) loadRegions();
    };

    navigator.geolocation.getCurrentPosition(
      (e) => {
        setLocalCordinates({
          latitude: e.coords.latitude,
          longitude: e.coords.longitude,
        })
      }
    );
  }, []);

  useEffect(() => {
    if (originCordinates) Cookies.set('@originCordinates', JSON.stringify(originCordinates));

    if (originAddress !== '') Cookies.set('@originAddress', originAddress);
  }, [originAddress, originCordinates])

  useEffect(() => {
    loadServicePrice();
  }, [originAddress, originCordinates, destinyAddress, destinyCordinates, selectedService, selectedPassenger, stops]);

  return (
    <div className="card">
      <Toast ref={toast} />

      {!is_callcenter ? (
        <div
          className="flex flex-column md:flex-row flex-wrap "
          style={{flexDirection: 'row'}}
        >
          <div className="card">
            <h5>Passageiro</h5>

            {loadingPassengers ? (
              <>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }} />
                <span> Carregando passageiros... </span>
              </>
            ) : (
              <Dropdown
                value={selectedPassenger}
                onChange={e => setSelectedPassenger(e.value)}
                options={passengers}
                optionLabel="name"
                placeholder="Selecione um passageiro"
              />
            )}
          </div>

          {!franchise_id ? (
            <div className="card">
              <h5>Franquia</h5>

              {loadingFranchises ? (
                <>
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }} />
                  <span> Carregando franquias... </span>
                </>
              ) : (
                <Dropdown
                  value={selectedFranchise}
                  onChange={e => setSelectedFranchise(e.value)}
                  options={franchises}
                  optionLabel="name"
                  placeholder="Selecione uma Franquia"
                />
              )}
            </div>
          ) : null}

          {!acting_region_id ? (
            <div className="card">
              <h5>Região</h5>

              {loadingRegions ? (
                <>
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }} />
                  <span> Carregando regiões... </span>
                </>
              ) : (
                <Dropdown
                  value={selectedRegion}
                  onChange={e => setSelectedRegion(e.value)}
                  options={regions}
                  optionLabel="name"
                  placeholder="Selecione uma Região"
                />
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="card">
        <h5>Origem</h5>
        <MapboxAutocomplete
          style={{width: '100%'}}
          publicKey={MAPBOX_KEY}
          inputClass="form-control search"
          country="br"
          resetSearch={false}
          placeholder={originAddress === '' ? "Digite o endereço de origem" : originAddress}
          onSuggestionSelect={(e) => {
            getOriginCordinates(e);
            setOriginAddress(e);
          }}
        />
        {/* <mapbox-search-box
          access-token={MAPBOX_KEY}
          proximity="0,0"
        >
          <input
            name="address" placeholder="Address" type="text"
            autoComplete="address-line1"
          />
        </mapbox-search-box> */}
        <Button
          label={"Definir local no mapa"}
          disabled={loading}
          icon={loading ? "pi pi-spin pi-spinner" : "pi pi-map-marker"}
          className="p-button-rounded p-button-outlined mr-2 mb-2"
          onClick={() => setOriginShowMap(true)}
        />
      </div>
      
      <div className="card">
        <h5>Destino</h5>
        <MapboxAutocomplete
          style={{width: '100%'}}
          publicKey={MAPBOX_KEY}
          inputClass="form-control search"
          country="br"
          resetSearch={false}
          placeholder={destinyAddress === '' ? "Digite o endereço de destino" : destinyAddress}
          onSuggestionSelect={(e) => {
            getDestinyCordinates(e);
            setDestinyAddress(e);
          }}
        />
        {/* <AddressAutofill
          accessToken={MAPBOX_KEY}
          browserAutofillEnabled
          confirmOnBrowserAutofill
          onRetrieve={(e) => {
            // getDestinyCordinates(e);
            // setDestinyAddress(e);
            console.log('e: ', e)
          }}
        >
          <input
            autoComplete="address-line1"
            type="text"
          />
        </AddressAutofill> */}
        <Button
          label={"Definir local no mapa"}
          disabled={loading}
          icon={loading ? "pi pi-spin pi-spinner" : "pi pi-map-marker"}
          className="p-button-rounded p-button-outlined mr-2 mb-2"
          onClick={() => setDestinyShowMap(true)}
        />
      </div>

      {/* Paradas Adicionais */}
      {_renderAddStopItem()}

      <div className="card">
        <h5>Selecione o serviço:</h5>
        <div className="card">
          <Carousel
            value={services}
            numVisible={3}
            numScroll={3}
            responsiveOptions={carouselResponsiveOptions}
            itemTemplate={_renderServices}
          />
        </div>
      </div>

      <div className="card">
        <h5>Forma de Pagamento:</h5>
        <div className="grid">
          <div className="col-12 md:col-4">
            <div className="field-radiobutton">
              <RadioButton
                inputId="payment1"
                name="payment"
                value="Dinheiro"
                checked={paymentType === "Dinheiro"}
                onChange={e => setPaymentType(e.value)}
              />
              <label htmlFor="payment1">Dinheiro</label>
            </div>
          </div>
          <div className="col-12 md:col-4">
            <div className="field-radiobutton">
              <RadioButton
                inputId="payment2"
                name="payment"
                value="PIX"
                checked={paymentType === "PIX"}
                onChange={e => setPaymentType(e.value)}
              />
              <label htmlFor="payment2">PIX</label>
            </div>
          </div>
          <div className="col-12 md:col-4">
            <div className="field-radiobutton">
              <RadioButton
                inputId="payment3"
                name="payment"
                value="Maquininha"
                checked={paymentType === "Maquininha"}
                onChange={e => setPaymentType(e.value)}
              />
              <label htmlFor="payment3">Maquininha</label>
            </div>
          </div>
        </div>
      </div>

      <h5>Dados do passageiro (Opcional)</h5>

      <div className="card">
        <div className="field text-left">
          <span className="p-input-icon-left">
            <i className="pi pi-user"></i>
            <InputText
              autoComplete="new-password"
              type="text"
              placeholder="Nome"
              value={passengerName}
              onChange={e => setPassegnerName(e.target.value)}
              autoFocus
            />
          </span>
        </div>
        <div className="field text-left">
          <span className="p-input-icon-left">
            <i className="pi pi-phone"></i>
            <InputText
              autoComplete="new-password"
              type="text"
              placeholder="Telefone"
              value={passengerPhone}
              onChange={e => setPassengerPhone(maskPhone(e.target.value))}
              autoFocus
            />
          </span>
        </div>
      </div>

      <div className="card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {race ? (
          <>
            <span style={{fontSize: '2rem', fontWeight: 'bold'}}>R$ {race.price}</span>
            <span>{distanteFormat(race.distance / 1000)} - {timeSecondsConvert(race.routeTime)}</span>
            <Button
              label={creatingBooking ? 'Pedindo corrida...' : 'PEDIR CORRIDA'}
              disabled={creatingBooking}
              className="mr-2 mb-2"
              style={{
                margin: 25,
                fontSize: '2rem',
              }}
              onClick={handleCreateBooking}
            />
          </>
        ) : (
          <>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', marginBottom: 20 }} />
            {loadMessage}
          </>
        )}
      </div>

      {/* Modal Mapa Origem */}
      <Dialog
        visible={originShowMap}
        style={{ width: "550px" }}
        header={'Local de embarque'}
        modal
        className="p-fluid"
        footer={originMapDialogFooter}
        onHide={hideDialog}
      >
        <div className="card">
          <Map
            latitude={originCordinates?.latitude || localCordinates.latitude}
            longitude={originCordinates?.longitude || localCordinates.longitude}
            callBack={setOriginCordinates}
          />
        </div>  
      </ Dialog>

      {/* Modal Mapa Destino */}
      <Dialog
        visible={destinyShowMap}
        style={{ width: "550px" }}
        header={'Destino'}
        modal
        className="p-fluid"
        footer={destinyMapDialogFooter}
        onHide={hideDialog}
      >
        <div className="card">
          <Map
            latitude={destinyCordinates?.latitude || localCordinates.latitude}
            longitude={destinyCordinates?.longitude || localCordinates.longitude}
            callBack={setDestinyCordinates}
          />
        </div>  
      </ Dialog>

      {/* Modal Mapa Parada Adicional */}
      <Dialog
        visible={stopShowMap}
        style={{ width: "550px" }}
        header={'Parada Adicional'}
        modal
        className="p-fluid"
        footer={stopMapDialogFooter}
        onHide={hideDialog}
      >
        <div className="card">
          <Map
            latitude={stops[stopIndex]?.latitude || localCordinates.latitude}
            longitude={stops[stopIndex]?.longitude || localCordinates.longitude}
            callBack={(cordinates: iCordinates) => {
              stops[stopIndex].latitude = cordinates.latitude;
              stops[stopIndex].longitude = cordinates.longitude;
            }}
          />
        </div>  
      </ Dialog>
    </div>
  );
};

export default Crud;

interface iCordinates {
  latitude: number,
  longitude: number,
}

interface iStop {
  address: string,
  latitude: number,
  longitude: number,
}
